import { isNil } from 'lodash';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';

import { isNilOrEmpty } from './objectUtils';
import { CustomValue, CustomValueDTO, Integration, Technic } from './types';

export const addOrReplaceCustomValue = (
  customValueToAdd: CustomValue,
  customValues?: CustomValue[] | CustomValueDTO[] | undefined | null
) => {
  if (isNilOrEmpty(customValues)) {
    return [customValueToAdd];
  }
  const cleanedCustomValues = customValues.reduce<CustomValue[]>((acc, currentCustomValue) => {
    if (currentCustomValue.name !== customValueToAdd.name) {
      acc.push({ name: currentCustomValue.name, value: currentCustomValue.value });
    }
    return acc;
  }, []);

  return [...cleanedCustomValues, customValueToAdd];
};

export const removeCustomValue = (
  customValues: CustomValue[] | CustomValueDTO[] | undefined | null,
  name: string
): CustomValue[] =>
  (customValues ?? []).reduce<CustomValue[]>((accumulator, currentCustomValue) => {
    if (currentCustomValue.name !== name) {
      accumulator.push({ name: currentCustomValue.name, value: currentCustomValue.value });
    }
    return accumulator;
  }, []);

export const withoutCustomValues = (
  customValues: CustomValue[] | CustomValueDTO[] | undefined | null,
  names: Set<string>
): CustomValue[] =>
  (customValues ?? []).filter(({ name }) => !names.has(name)).map(({ name, value }) => ({ name, value }));

export const replaceCustomValueWhenDefined = (
  customValues: CustomValue[] | null | undefined,
  name: string,
  value: string | undefined
) => {
  if (isNil(value)) {
    return customValues;
  }

  return addOrReplaceCustomValue({ name, value }, customValues);
};

export const getIntegrationPrefix = (integration: Integration) => `INTEGRATION_${integration.id}`;

export const customValueNameWithPrefix = (customValueName: string, integrationPrefix: string) =>
  `${integrationPrefix}_${customValueName}`;

export const findCustomValue = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined,
  name: string
): string | null => customValues?.find((customValue) => customValue.name === name)?.value ?? null;

export const findCustomValues = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined,
  names: string[]
): CustomValue[] =>
  names.reduce<CustomValue[]>((acc, name) => {
    const value = findCustomValue(customValues, name);
    if (!isNil(value)) {
      acc.push({ name, value });
    }
    return acc;
  }, []);

export const hasAllCustomValuesDefined = (customValues: CustomValue[] | null | undefined, names: string[]) => {
  const customValuesObject = Object.fromEntries((customValues ?? []).map(({ name, value }) => [name, value]));
  return names.every((name) => !isEmpty(customValuesObject[name]));
};

export const serializeCustomFieldFileId = (fileId: string | null): string => fileId ?? '';

export const parseCustomFieldFileId = (value: string | null | undefined): string | null =>
  isNilOrEmpty(value) ? null : value;

export const getCustomFieldFileIds = (technic: Pick<Technic, 'customValues'>): string[] =>
  compact((technic.customValues ?? []).map(({ value }) => parseCustomFieldFileId(value)));

/** The files custom values referenced before a save and no longer reference after it. */
export const getDroppedCustomFieldFileIds = (
  previousTechnic: Pick<Technic, 'customValues'>,
  savedTechnic: Pick<Technic, 'customValues'>
): string[] => {
  const savedFileIds = getCustomFieldFileIds(savedTechnic);
  return getCustomFieldFileIds(previousTechnic).filter((fileId) => !savedFileIds.includes(fileId));
};
